import { PrimaryButton } from '@changegps/horizon-component-library';
import { Box, Grid, Typography } from '@mui/material';

import { ControlledInputs } from '@/components';
import {
  bodyFontSizeOptions,
  followerBottomMarginOptions,
  followerTopMarginOptions,
  fontStyleOptions,
  headerFontSizeOptions,
  letterHeadBottomMarginOptions,
  letterheadStyleOptions,
  letterHeadTopMarginOptions,
  salutationOptions,
} from '@/features/settings/hooks/constants';

import { useGlobalLetterFormContext } from '../hooks';
import { GlobalLetter } from '../types';

const contentFields: {
  label: string;
  name: { font_size: keyof GlobalLetter; font_style: keyof GlobalLetter };
  options: {
    font_size: { label: string; value: string | number }[];
    font_style: { label: string; value: string | number }[];
  };
}[] = [
  {
    label: 'Header Content',
    name: { font_size: 'header_font_size', font_style: 'header_font' },
    options: { font_size: headerFontSizeOptions, font_style: fontStyleOptions },
  },
  {
    label: 'Body Content',
    name: { font_size: 'font_size', font_style: 'font' },
    options: { font_size: bodyFontSizeOptions, font_style: fontStyleOptions },
  },
];

const marginFields: {
  label: string;
  name: keyof GlobalLetter;
  options: { label: string; value: string | number }[];
  desc: string;
}[] = [
  {
    label: 'Letterhead Top Margin',
    name: 'top_margin',
    options: letterHeadTopMarginOptions,
    desc: 'Number of line breaks inserted before letter content.',
  },
  {
    label: 'Letterhead Bottom Margin',
    name: 'bottom_margin',
    options: letterHeadBottomMarginOptions,
    desc: 'Number of line breaks in footer.',
  },
  {
    label: 'Follower Top Margin',
    name: 'follower_top_margin',
    options: followerTopMarginOptions,
    desc: 'Number of line breaks inserted before follower content.',
  },
  {
    label: 'Follower Bottom Margin',
    name: 'follower_bottom_margin',
    options: followerBottomMarginOptions,
    desc: 'Number of line breaks after follower content.',
  },
  {
    label: 'Address/Salutation Spacing',
    name: 'salutation_margin',
    options: salutationOptions,
    desc: 'Number of line breaks between the address block and the salutation.',
  },
];

interface FormattingProps {
  handleSave: () => void;
  isPending: boolean;
}

export const Formatting = ({ handleSave, isPending }: FormattingProps) => {
  const form = useGlobalLetterFormContext();

  return (
    <Box sx={{ padding: '24px' }}>
      <Grid container spacing={3}>
        {contentFields.map(({ label, name, options }, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {label}
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <ControlledInputs.Select
                  ControllerProps={{
                    control: form.control,
                    name: name.font_size,
                  }}
                  optionKeys={{ label: 'label', value: 'value' }}
                  options={options.font_size}
                  label="Size"
                  fullWidth
                />
              </Grid>
              <Grid item>
                <ControlledInputs.Select
                  ControllerProps={{
                    control: form.control,
                    name: name.font_style,
                  }}
                  optionKeys={{ label: 'label', value: 'value' }}
                  options={options.font_style}
                  label="Font-Style"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        ))}

        {marginFields.map(({ label, name, options, desc }, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {label}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3}>
                <ControlledInputs.Select
                  ControllerProps={{ control: form.control, name }}
                  optionKeys={{ label: 'label', value: 'value' }}
                  options={options}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={9}>
                <Typography variant="body2" color="textSecondary">
                  {desc}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Letterhead Style
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <ControlledInputs.Select
                ControllerProps={{
                  control: form.control,
                  name: `merge_template`,
                }}
                optionKeys={{ label: 'label', value: 'value' }}
                options={letterheadStyleOptions}
                fullWidth
              />
            </Grid>
            <Grid item xs={5.5}>
              <Typography variant="body2" color="textSecondary">
                Specify what type of letterhead your firm uses.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Misc.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={2}>
              <ControlledInputs.CheckboxWithLabel
                ControllerProps={{ control: form.control, name: `page_num` }}
                FormControlLabelProps={{ label: '' }}
              />
            </Grid>
            <Grid item xs={10}>
              <Typography variant="body2" color="textSecondary">
                Include Page Number on Follower Pages
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px' }}
      >
        <PrimaryButton onClick={handleSave} disabled={isPending}>
          Save Changes
        </PrimaryButton>
      </Box>
    </Box>
  );
};
